<script lang="ts">
    // Jet/Svelte/Models
    import { ContentKind } from '~/jet/models';
    import { createEventDispatcher } from 'svelte';

    // Types
    import type { PlaybackItemLayout } from '~/components/structure/Players/components/MobileNowPlaying/types';
    import type { QueueItemMetadata } from '~/jet/models';

    // Utilities
    import { getPlayback } from '~/utils/playback';
    import { toggleFavorite } from '~/utils/favorites';

    // Components
    import PlaybackItemArtwork from '~/components/structure/Players/components/MobileNowPlaying/PlaybackItemArtwork.svelte';
    import NowPlayingMetadata from '~/components/structure/Players/components/NowPlayingMetadata.svelte';

    // Stores
    import { getI18n } from '~/stores/i18n';

    const dispatch = createEventDispatcher();
    const i18n = getI18n();
    const { queue } = getPlayback();

    export let item: QueueItemMetadata | null = null;
    export let layout: PlaybackItemLayout = 'large';
    export let artworkButtonAriaLabel: string | null = null;

    // Updates contextual menu config to render the <amp-contextual-menu />
    // element somewhere other than the default of document.body
    export let parentElement: HTMLElement = null;

    $: isPaused = $queue.derivedStates.isPaused;
    $: isStopped = $queue.derivedStates.isStopped;
    $: isEmpty = !item;
</script>

<div
    data-testid="now-playing-item"
    class="playback-item"
    class:playback-item--large={layout === 'large'}
    class:playback-item--landscape={layout === 'landscape'}
    class:playback-item--compact={layout === 'compact'}
    class:is-paused={isPaused || isStopped}
    class:is-empty={isEmpty}
>
    <div class="artwork-container">
        <button
            data-testid="now-playing-artwork-button"
            class="artwork-button"
            on:click={() => dispatch('artworkClick')}
            aria-label={artworkButtonAriaLabel}
            disabled={layout !== 'compact' && !item}
        >
            <PlaybackItemArtwork {item} {layout} />
        </button>
    </div>

    <div class="metadata">
        <div class="metadata-container">
            <div class="metadata__song-details">
                {#if item}
                    <NowPlayingMetadata {item} bind:parentElement />
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/colors' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'ac-sasskit/core/locale' as *;
    @use '../../../../../styles/music-classical/core/globalvars' as *;

    $compact-item-height: 66px;
    $accessory-buttons-width: 72px;
    $artwork-gutter: 24px;
    $metadata-gutter: 32px;

    .playback-item {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;

        :global(.lcd-meta) {
            --lcd-height: 48px;
            --lcd-justify-text: start;
            --lcd-primary-text-color: var(--systemPrimary-onDark);
            --lcd-secondary-text-color: var(--systemSecondary-onDark);
            --lcd-meta-icon-color: var(--systemSecondary-onDark);
            width: calc(100% - #{$accessory-buttons-width});
            margin-inline-start: -10px;

            :global(.lcd-meta__primary) {
                font: var(--title-2-emphasized);
            }

            :global(.lcd-meta__secondary) {
                font: var(--title-2);
            }

            :global(.lcd-meta__secondary .lcd-meta-line-wrapper) {
                position: initial;
                height: initial;
            }
        }

        // High contrast style overrides
        @media (prefers-contrast: more) {
            :global(.lcd-meta) {
                --lcd-primary-text-color: var(--systemPrimary);
                --lcd-secondary-text-color: var(--keyColor);
                --lcd-meta-icon-color: var(--systemSecondary);
            }
        }
    }

    .artwork-container {
        line-height: 0; // Normalize height
        position: relative;
        z-index: var(--z-default);
    }

    .playback-item--large {
        .artwork-container {
            width: 100vw;
            height: 100%;
            display: grid;
            align-content: center;
            transition: transform 500ms cubic-bezier(0.76, 0.665, 0.37, 1.35);

            // Unlikely edge case of short, non-standard (squarish) mobile screen
            @media (--grid-layout-xs-2) and (height <= 860px) {
                transform: scale(0.9);
            }
        }

        // Shrink artwork by 27% when queue is paused or empty
        &.is-paused,
        &.is-empty {
            .artwork-container {
                transform: scale(0.73);
                transition: transform 350ms;
            }
        }

        .accessory-buttons {
            position: initial;
        }
    }

    .artwork-button {
        // Prevent highlighting artwork on tap
        -webkit-tap-highlight-color: transparent;
        display: contents;
    }

    .metadata {
        display: flex;
        margin: 0 $metadata-gutter 18px;
    }

    .metadata-container {
        width: 100%;
        position: relative;
    }

    .metadata__attribution {
        margin-bottom: 3px;
    }

    .metadata__song-details {
        position: relative;
        display: flex;
        justify-content: space-between;
    }

    .accessory-buttons {
        --contextMenuCircleFillOverride: var(--systemQuaternary-onDark);
        --favoriteButtonSize: 28px;
        --favoriteButtonBackground: var(--systemQuaternary-onDark);
        --favoriteButtonStarOutline: var(--systemPrimary-onDark);
        --favoriteButtonStarCutout: var(--systemSecondary-onDark);
        --favoriteButtonStarFill: transparent;

        // Reset Favorite button hover styles to match non-hover colors
        --favoriteButtonBackground-hover: var(--favoriteButtonBackground);
        --favoriteButtonStarOutline-hover: var(--favoriteButtonStarOutline);
        --favoriteButtonStarCutout-hover: var(--favoriteButtonStarCutout);
        --favoriteButtonStarFill-hover: var(--favoriteButtonStarFill);

        // Position inside of now-playing-item
        position: absolute;
        right: 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        inset-inline-end: 0;

        :global(amp-contextual-menu-button) {
            // Remove default margin on contextual menu button
            margin-inline-start: 0;
        }
    }

    // Compact and landscape style overrides
    .playback-item--compact,
    .playback-item--landscape {
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        height: $compact-item-height;
        gap: 12px;

        .artwork-container {
            margin: 0;
        }

        .metadata {
            margin: 0;
            flex: 1;
            overflow: hidden;

            @include overflow-bleed($focus-size);
        }

        .accessory-buttons {
            flex: 0 $accessory-buttons-width;
        }
    }

    .playback-item--compact {
        .artwork-container {
            flex: 0 $compact-artwork-size;
        }
    }

    // Landscape style overrides
    .playback-item--landscape {
        height: 100%;
        margin: 0 32px;
        padding-bottom: 20px;

        .artwork-container {
            height: 100%;
            width: auto;
            aspect-ratio: 1;
        }

        :global(.lcd-meta) {
            --lcd-justify-text: center;
        }

        // TODO: rdar://110324318 ([Mobile Queue Parity] Allow contextual menu to use mobile styles in landscape mode)
        .accessory-buttons {
            display: none;
        }
    }

    // High contrast style overrides
    @media (prefers-contrast: more) {
        .accessory-buttons {
            --contextMenuEllipsisFillOverride: var(--keyColor);
            --contextMenuCircleFillOverride: initial;
        }
    }
</style>
